import {Button, Modal, Tooltip} from 'antd';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import {FC, Fragment, memo, useState} from 'react';
import {useSession} from '../../contexts/Session';
import {WakandaService} from '../../util/fetch-wakanda';
import {getMomentFromTimestamp} from '../../util/render';
import {
  cordColorLabels,
  cordColorRules,
  CORD_COLOR,
  deporteOptionLabels,
  deporteOptionsRules,
  DEPORTE_OPTION,
  earbackColorationLabels,
  earbackColorationRules,
  EARBACK_COLORATION,
  earWaxShieldLabels,
  earWaxShieldRules,
  EARWAX_SHIELD,
  EVENT,
  eventLabels,
  eventRules,
  MISCELLANEOUS,
  miscellaneousLabels,
  miscellaneousRules,
  OPTION,
  optionEmboutLabelRules,
  optionLabels,
  optionRules,
  optionTubeLabels,
  optionTubeRules,
  OPTION_TUBE,
  TUBE,
  tubeLabels,
  tubeRules,
} from '../Options/data';
import {Client, Order} from '../Orders/data';
import {Article} from './util';

const {REACT_APP_SITE_NAME = 'nimes'} = process.env;

export const loadLogo = (): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = '/EAROW.png';
    img.onload = () => {
      resolve(img);
    };
    img.onerror = () => {
      reject('pcq');
    };
  });
};

export const loadLocalLogo = (): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = `/2022/${REACT_APP_SITE_NAME}.png`;
    img.onload = () => {
      resolve(img);
    };
    img.onerror = () => {
      reject('pcq');
    };
  });
};

export const loadQRCode = (text: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    QRCode.toDataURL(text, {errorCorrectionLevel: 'H'}).then((url) => {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        resolve(img);
      };
      img.onerror = () => {
        reject('pcq');
      };
    });
  });
};

export const loadSchema = (): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = `/schema.png`;
    img.onload = () => {
      resolve(img);
    };
    img.onerror = () => {
      reject('pcq');
    };
  });
};

const generate = async (
  session: WakandaService,
  commande: Order,
): Promise<string> => {
  const client =
    (await session.fetch<Client>('Clients', 'getClient', {
      // eslint-disable-next-line @typescript-eslint/camelcase
      id_client: commande.id_client.__KEY,
    })) || null;

  const img = await loadLogo();
  const localImg = await loadLocalLogo();
  const schema = await loadSchema();
  let qr;
  if (commande.numeroBon) {
    qr = await loadQRCode(commande.numeroBon);
  }

  const doc = new jsPDF();

  if (!commande.ID) {
    doc.setFontSize(90);
    doc.setTextColor('#DDDDDD');
    doc.setFont('helvetica', 'normal');
    doc.text('Document temporaire', 30, 280, {angle: 57});
  }
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor('#000000');

  // logo
  doc.addImage(img, 'PNG', 10, 10, 30, 10, undefined, 'FAST');

  switch (REACT_APP_SITE_NAME) {
    case 'nimes':
      doc.addImage(localImg, 'PNG', 45, 11, 10, 7, undefined, 'FAST');
      break;
    case 'paris':
      doc.addImage(localImg, 'PNG', 45, 11, 8, 7, undefined, 'FAST');
      break;
    case 'lyon':
      doc.addImage(localImg, 'PNG', 45, 11, 5, 7, undefined, 'FAST');
      break;
  }

  // qrcode
  if (commande.numeroBon) {
    doc.addImage(qr, 'PNG', 125, 260, 25, 25, undefined, 'FAST');
  }

  // schema
  doc.addImage(schema, 'PNG', 160, 255, 40, 40, undefined, 'FAST');

  doc.setFont('helvetica', 'bold');
  switch (REACT_APP_SITE_NAME) {
    case 'nimes':
      doc.text('Nîmes', 60, 13);
      doc.text('6046 Chemin du Moulin de Carrière - 30250 Aubais', 60, 18);
      break;
    case 'paris':
      doc.text('Paris', 60, 13);
      doc.text('19, rue des Petites Ecuries - 75010 Paris', 60, 18);
      break;
    case 'lyon':
      doc.text('Lyon', 60, 13);
      doc.text('2, impasse Maurice Ravel - 69330 Jonage', 60, 18);
      break;
  }

  // header top-right
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('COMMANDE', 200, 15, {align: 'right'});
  doc.setFontSize(10);
  if (commande.numeroBon) {
    doc.text(`Réf. : ${commande.numeroBon}`, 200, 20, {align: 'right'});
  }

  const numeroCommandeClient = commande.numeroAffaire
    ? `N° de commande Client : ${commande.numeroAffaire}`
    : '';
  if (client) {
    doc.text(
      `Code client : ${client.code}` +
        (numeroCommandeClient ? ` / ${numeroCommandeClient}` : ''),
      200,
      25,
      {align: 'right'},
    );
  } else if (numeroCommandeClient) {
    doc.text(numeroCommandeClient, 200, 25, {align: 'right'});
  }

  doc.setFont('helvetica', 'normal');
  doc.text(
    `Date de commande : ${getMomentFromTimestamp(commande.dateSaisie).format(
      'L',
    )}`,
    200,
    30,
    {align: 'right'},
  );
  doc.setFont('helvetica', 'bold');
  doc.text('Patient : ' + commande.patient, 200, 35, {align: 'right'});
  doc.setFontSize(12);

  // client & delivery
  doc.setFontSize(14);
  doc.text('CLIENT', 13, 43);
  doc.setFontSize(10);
  doc.text(client?.code, 33, 43);
  doc.setFontSize(14);
  doc.text('ADRESSE DE LIVRAISON', 111, 43);
  doc.setFillColor('#F0F0F0');
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  doc.setLineDash([1]);
  doc.roundedRect(10, 45, 92, 35, 3, 3, 'FD');
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  doc.roundedRect(108, 45, 92, 35, 3, 3);
  doc.setFontSize(10);
  doc.text(client?.raisonSociale || '', 12, 50);
  doc.text(commande.livr_raisonSociale || '', 110, 50);

  doc.setFont('helvetica', 'normal');
  doc.text(client?.adresseGenerale || '', 12, 55);
  doc.text(commande.livr_adresse || '', 110, 55);
  doc.text(`${client?.codePostal || ''} ${client?.ville || ''}`, 12, 65);
  doc.text(`${commande.livr_codePostal} ${commande.livr_ville}`, 110, 65);
  doc.setFontSize(9);
  doc.text(`Tél : ${client?.tel || ''}`, 12, 70);
  doc.text(`E-mail : ${client?.email || ''}`, 12, 75);
  doc.text(`E-mail : ${commande.email}`, 110, 75);

  const labels = {
    categorie: 'Catégorie',
    produit: 'Produit',
    passtopProductType: 'Type',
    matiere: 'Matière',
    forme: 'Forme',
    marque: 'Marque',
    deporteBrand: 'Marque',
    ecouteur: 'Écouteur',
    bague: 'Bague',
    formeEpaulement: 'Epaulement',
    couleur: 'Couleur',
    event: 'Event',
    eventDiam: 'Diamètre event',
    eventSurMesure: 'Diamètre event (sur mesure)',
    finition: 'Finition',
    tube: 'Tube',
    optionEmbout: 'Options embout',
    repereCouleur: 'Repère de couleur',
    otoscan: 'OTOSCAN Otometrics',
    optionsEcouteur: 'Pare-Cérumen',
    vernisAntibacterien: 'Vernis antibactérien',
    optionAquason: 'Option',
    optionEP2: 'Option',
    optionCordonEP2: 'Couleur cordon',
    optionGravureEP2: 'Gravure',
    optionPianissimo: 'Options',
    optionPasstopT: 'Options',
    optionPasstopNeo: 'Options',
    optionTir: 'Option',
    optionPasstopSansPoignee: 'Sans poignée (sans cordon)',
    sousGarantie: 'Sous garantie',
    tubeCouleur: 'Tube',
    optionDeporte: 'Options',
    optionIros: 'Option Iros',
    earbackColorOption: 'Coloration',
    earbackOption: 'Option',
    optionPasstopO: 'Options',
    optionObturateur: 'Options',
    typeEmbout: 'Option',
    optionSweetNight: 'Option',
  };

  const bagueIds: [string | null, string | null] = [null, null];
  const bagues: [Article | null, Article | null] = [null, null];

  const articleIds: string[] = [];
  // eslint-disable-next-line array-callback-return
  ['OG', 'OD'].map((ear, index) => {
    const detailsBague = commande.details?.[ear as 'OG' | 'OD']?.bague;
    if (detailsBague) {
      bagueIds[index] = detailsBague;
    }

    const articleId = commande.details?.[ear as 'OG' | 'OD']?.article;
    if (articleId) {
      articleIds.push(articleId);
    }
  });

  const articles =
    articleIds.length > 0
      ? (await session.fetch<Article[]>('Articles', 'getArticlesFromIDs', {
          ids: Array.from(new Set(articleIds)),
        })) || []
      : [];

  // eslint-disable-next-line id-length
  const baguesIdsArr = Array.from(new Set(bagueIds)).filter((v) => v !== null);
  const baguesArts =
    baguesIdsArr.length > 0
      ? (await session.fetch<Article[]>('Articles', 'getArticlesFromIDs', {
          ids: baguesIdsArr,
        })) || []
      : [];

  // eslint-disable-next-line array-callback-return
  bagueIds.map((id, index) => {
    const bagueArt = baguesArts.find(({ID}) => ID === id);
    if (bagueArt) {
      bagues[index] = bagueArt;
    }
  });

  const earDetails = async (
    ear: 'OG' | 'OD',
    doc: jsPDF,
    height: number,
  ): Promise<number> => {
    if (!commande.details) {
      return height;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const details = (commande.details as any)[ear];

    if (!details) {
      return height;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor('#000000');
    doc.text(ear, 11, height);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('1', 190, height, {align: 'center'});
    doc.setTextColor('#555555');
    height += 4;

    console.log(details);

    Object.entries(labels).map((entry): void => {
      const [key, label] = entry;
      if (!details[key]) {
        return undefined;
      }

      let value = '';

      switch (key) {
        case 'categorie':
          value = details[key].replace(/\n+/g, ' / ');
          break;
        case 'produit': {
          value = details[key];

          const isPasStop = value.includes('Le PasStop ® O.R : Résine');
          if (isPasStop) {
            const articleId = details['article'];
            // eslint-disable-next-line id-length
            const article = articles.find((v) => v.ID === articleId);
            const articleLabel = article?.libelle;
            if (articleLabel) {
              value = details[key] + ' (' + articleLabel + ')';
            }
          }

          const isPasStopNEO =
            value.includes('passTOP NEO S') || value.includes('passTOP NEO R');
          if (isPasStopNEO) {
            const articleId = details['article'];
            // eslint-disable-next-line id-length
            const article = articles.find((v) => v.ID === articleId);
            const articleLabel = article?.libelle;
            if (articleLabel) {
              value = details[key] + ' (' + articleLabel + ')';
            }
          }

          break;
        }
        case 'bague':
          if (bagues && bagues.length > 0) {
            value = bagues[ear === 'OG' ? 0 : 1]?.libelle || '';
          }
          break;
        case 'marque':
          if (bagues && bagues.length > 0) {
            value = commande.details[ear]?.deporteBrand || '';
          }
          value = '';
          break;
        case 'repereCouleur':
        case 'otoscan':
        case 'vernisAntibacterien':
        case 'sousGarantie':
        case 'optionGravureEP2':
        case 'optionPasstopSansPoignee':
          value = details[key] ? 'Oui' : 'Non';
          break;
        case 'tube':
          if (details.tubeCouleur) break;

          const tubeEntry = Object.entries(tubeRules).find((tubeRule) =>
            details[key].includes(tubeRule[1]),
          );
          if (tubeEntry) {
            value = tubeLabels[tubeEntry[0] as TUBE];
          }
          break;
        case 'optionEmbout': {
          const values: string[] = [];
          details[key].map((detail: string): void => {
            const optionTubeEntry = Object.entries(
              optionTubeRules,
            ).find((optionTubeRule) => detail.includes(optionTubeRule[1]));
            if (optionTubeEntry) {
              values.push(optionTubeLabels[optionTubeEntry[0] as OPTION_TUBE]);
              return undefined;
            }

            const optionEmboutEntry = Object.entries(
              optionEmboutLabelRules,
            ).find((optionTubeRule) =>
              detail.includes(optionTubeRule[1] || ''),
            );
            if (optionEmboutEntry) {
              values.push(optionLabels[optionEmboutEntry[0] as OPTION]);
            }

            return undefined;
          });
          value = values.join(', ');
          break;
        }
        case 'eventDiam':
        case 'eventSurMesure':
          value = `${details[key]} mm`;
          break;
        case 'event':
          const eventEntry = Object.entries(eventRules).find((eventRule) =>
            details[key].includes(eventRule[1]),
          );
          if (eventEntry) {
            value = eventLabels[eventEntry[0] as EVENT];
          }
          break;
        case 'optionsEcouteur': {
          const values: string[] = [];
          details[key].map((detail: string): void => {
            if (detail.includes('OPT_montage')) {
              values.push('Montage écouteur');
              return undefined;
            }

            const miscellaneousEntry = Object.entries(miscellaneousRules).find(
              (miscellaneousRule) =>
                miscellaneousRule[1] && detail.includes(miscellaneousRule[1]),
            );
            if (miscellaneousEntry) {
              values.push(
                miscellaneousLabels[miscellaneousEntry[0] as MISCELLANEOUS],
              );
              return undefined;
            }

            const earWaxEntry = Object.entries(earWaxShieldRules).find(
              (earWaxShieldRule) =>
                earWaxShieldRule[1] && detail.includes(earWaxShieldRule[1]),
            );
            if (earWaxEntry) {
              values.push(earWaxShieldLabels[earWaxEntry[0] as EARWAX_SHIELD]);
            }

            return undefined;
          });
          value = values.join(', ');
          break;
        }
        case 'optionDeporte': {
          const values: string[] = [];
          details[key].map((detail: string): void => {
            const optionDeporteEntry = Object.entries(deporteOptionsRules).find(
              (deporteOptionsRule) =>
                deporteOptionsRule[1] && detail.includes(deporteOptionsRule[1]),
            );
            if (optionDeporteEntry) {
              if (
                (optionDeporteEntry[0] === DEPORTE_OPTION.LEFT_RIGHT_MARK ||
                  optionDeporteEntry[0] === DEPORTE_OPTION.CUSTOM_MARK) &&
                details.customMark
              ) {
                values.push(`Autre marquage: "${details.customMark}"`);
              } else {
                const ruleLabel =
                  deporteOptionLabels[optionDeporteEntry[0] as DEPORTE_OPTION];
                if (ruleLabel) {
                  values.push(ruleLabel);
                }
              }
            }

            return undefined;
          });
          value = values.join(', ');
          break;
        }
        case 'optionIros': {
          switch (details[key]) {
            case 'iros':
              value = 'Iros';
              break;
            case 'demiIros':
              value = 'Demi-Iros';
              break;
          }
          break;
        }
        case 'optionEP2': {
          // const optionEntry = Object.entries(optionRules).find(
          //   (optionRule) =>
          //     optionRule[1] && details[key].includes(optionRule[1]),
          // );
          // if (optionEntry) {
          //   value = optionLabels[optionEntry[0] as OPTION];
          // }

          const values: string[] = [];
          details[key].map((detail: string): void => {
            const optionEntry = Object.entries(optionRules).find(
              (optionRule) => optionRule[1] && detail.includes(optionRule[1]),
            );
            if (optionEntry) {
              values.push(optionLabels[optionEntry[0] as OPTION]);
            }

            return undefined;
          });
          value = values.join(', ');

          break;
        }
        case 'optionCordonEP2': {
          const cordColorEntry = Object.entries(cordColorRules).find(
            (cordColorRule) =>
              cordColorRule[1] && details[key].includes(cordColorRule[1]),
          );
          if (cordColorEntry) {
            value = cordColorLabels[cordColorEntry[0] as CORD_COLOR];
          }
          break;
        }
        case 'optionPianissimo':
          const values: string[] = [];
          details[key].map((detail: string): void => {
            const optionEntry = Object.entries(optionRules).find(
              (optionRule) => optionRule[1] && detail.includes(optionRule[1]),
            );
            if (optionEntry) {
              values.push(optionLabels[optionEntry[0] as OPTION]);
            }

            return undefined;
          });
          value = values.join(', ');
          break;
        case 'optionSweetNight':
          value = details[key];
          break;
        case 'tubeCouleur':
          value = `Coloré ${details[key]}`;
          break;
        case 'optionPasstopT': {
          const values: string[] = [];
          details[key].map((detail: string): void => {
            const optionEntry = Object.entries(optionRules).find(
              (optionRule) => optionRule[1] && detail.includes(optionRule[1]),
            );
            if (optionEntry) {
              values.push(optionLabels[optionEntry[0] as OPTION]);
            }

            return undefined;
          });
          value = values.join(', ');
          break;
        }
        case 'optionTir':
          if (details[key].includes('OPT_option01')) {
            value = 'Camouflage';
          }
          break;
        case 'earbackColorOption': {
          const optionEntry = Object.entries(earbackColorationRules).find(
            (optionRule) =>
              optionRule[1] && details[key].includes(optionRule[1]),
          );
          if (optionEntry) {
            value =
              earbackColorationLabels[optionEntry[0] as EARBACK_COLORATION];
          }
          break;
        }

        case 'optionAquason': {
          const optionEntry = Object.entries(optionRules).find(
            (optionRule) =>
              optionRule[1] && details[key].includes(optionRule[1]),
          );
          if (optionEntry) {
            value = optionLabels[optionEntry[0] as OPTION];
          }
          break;
        }
        case 'optionObturateur': {
          const values: string[] = [];
          details[key].map((detail: string): void => {
            const optionEntry = Object.entries(optionRules).find(
              (optionRule) => optionRule[1] && detail.includes(optionRule[1]),
            );
            if (optionEntry) {
              values.push(optionLabels[optionEntry[0] as OPTION]);
            }

            return undefined;
          });
          value = values.join(', ');
          break;
        }
        case 'optionPasstopNeo': {
          const values: string[] = [];
          details[key].map((detail: string): void => {
            const optionEntry = Object.entries(optionRules).find(
              (optionRule) => optionRule[1] && detail.includes(optionRule[1]),
            );
            if (optionEntry) {
              values.push(optionLabels[optionEntry[0] as OPTION]);
            }

            return undefined;
          });
          value = values.join(', ');
          break;
        }
        case 'earbackOption': {
          const values: string[] = [];
          details[key].map((detail: string): void => {
            const optionEntry = Object.entries(optionRules).find(
              (optionRule) => optionRule[1] && detail.includes(optionRule[1]),
            );
            if (optionEntry) {
              values.push(optionLabels[optionEntry[0] as OPTION]);
            }

            return undefined;
          });
          value = values.join(', ');
          break;
        }
        case 'typeEmbout': {
          value = details[key].charAt(0).toUpperCase() + details[key].slice(1);
          break;
        }
        default:
          value = details[key];
          break;
      }

      if (!value) {
        return undefined;
      }

      console.log(key, `${label} : ${value}`);
      const text = `${label} : ${value}`;
      doc.text(text, 15, height);
      height += 5;

      return undefined;
    });
    return height + 5;
  };

  let height = 104;
  height = await earDetails('OG', doc, height);
  height = await earDetails('OD', doc, height);

  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  doc.setLineDash();
  doc.rect(10, 85, 190, height - 85);
  doc.rect(180, 95, 20, height - 100);
  doc.setFillColor('#000000');
  doc.rect(10, 90, 190, 5, 'F');
  doc.rect(10, height - 5, 190, 5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor('#FFFFFF');
  doc.text('Désignation', 11, 94);
  doc.text('Qté', 190, 94, {align: 'center'});

  height += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor('#000000');

  const files = commande.details.originalFiles || commande.details.files;

  if (files?.length > 0) {
    doc.text("Fichiers d'empreintes : " + files.length, 10, height);
    height += 5;

    doc.setFontSize(9);
    doc.text(files.map((file) => `"${file}"`).join(', '), 10, height);
    height += 3;

    doc.setFontSize(11);
  }

  //comments
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  doc.setLineDash([1]);
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  doc.roundedRect(10, height, 100, 30, 3, 3);
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  doc.setLineDash();

  height += 5;

  doc.text('Commentaires', 46, height);

  doc.text(commande.messageInterne || '', 12, height + 5, {maxWidth: 90});

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);

  height += 3;

  /* doc.text('PROCÉDURE', 115, height);
  doc.text("1. J'imprime le bon de commande", 115, height + 5);
  doc.text('2. Je joins les empreintes physiques', 115, height + 10);
  doc.text("3. J'insère dans le colis remis au transporteur", 115, height + 15);*/

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6);

  switch (REACT_APP_SITE_NAME) {
    case 'nimes':
      doc.text(
        '6046 Chemin du Moulin de Carrière - 30250 Aubais - Téléphone: 04 66 80 22 89',
        105,
        290,
        {align: 'center'},
      );
      doc.text(
        'www.interson-protac.com - audio@interson-protac.com',
        105,
        293,
        {
          align: 'center',
        },
      );
      break;
    case 'paris':
      doc.text(
        '19, rue des Petites Ecuries - 75010 Paris - Téléphone: 01 47 70 97 11',
        105,
        290,
        {align: 'center'},
      );
      doc.text(
        'www.interson-protac.com - paris@interson-protac.com',
        105,
        293,
        {
          align: 'center',
        },
      );
      break;
    case 'lyon':
      doc.text(
        '2, impasse Maurice Ravel - 69330 Jonage - Téléphone: 04 72 37 12 70',
        105,
        290,
        {align: 'center'},
      );
      doc.text('www.interson-protac.com - lyon@interson-protac.com', 105, 293, {
        align: 'center',
      });
      break;
  }

  doc.setFont('helvetica', 'normal');

  return doc.output('bloburi');
};

interface Props {
  large?: boolean;
  disabled?: boolean;
  order: Partial<Order>;
}

const DocumentModal: FC<Props> = ({large, disabled, order}) => {
  const session = useSession();

  const [dataUri, setDataUri] = useState('');
  const [loading, setLoading] = useState(false);

  const hideModal = (): void => {
    setDataUri('');
  };

  const getPDF = async (): Promise<void> => {
    setLoading(true);
    const dataUri = await generate(session, order as Order);
    setLoading(false);
    setDataUri(dataUri);
  };

  return (
    <Fragment>
      <Tooltip
        title={!disabled ? 'Télécharger un export PDF de la commande' : null}
      >
        <Button
          type="primary"
          ghost
          disabled={disabled}
          shape={large ? undefined : 'circle'}
          style={{margin: '0 8px', ...(large ? {} : {border: 'none'})}}
          onClick={getPDF}
          loading={loading}
        >
          {large ? (
            <span style={{textTransform: 'uppercase'}}>Imprimer Commande</span>
          ) : (
            <img src="/2022/bouton-telechargement.svg" alt="Télécharger" />
          )}
        </Button>
      </Tooltip>
      <Modal
        width="90vw"
        visible={dataUri.length > 0}
        footer={[
          <a key="download" href={dataUri} download={`${order.patient}.pdf`}>
            <Button>Télécharger</Button>
          </a>,
          <Button
            key="close"
            type="primary"
            onClick={hideModal}
            style={{marginLeft: 8}}
          >
            Fermer
          </Button>,
        ]}
      >
        <div style={{height: '80vh'}}>
          {dataUri.length > 0 ? (
            <iframe
              title="PDF"
              src={`/pdf/web/viewer.html?file=${dataUri}`}
              width="100%"
              height="100%"
              style={{border: 0}}
            />
          ) : null}
        </div>
      </Modal>
    </Fragment>
  );
};

export default memo(DocumentModal);
